import { Site, SiteChangeRequest, SignalBoxChangeRequest, FibreChangeRequest } from '../types/Site';
import { supabase } from '../lib/supabase';
import { osGridToLatLng, ukGridToLatLng } from './coordinateUtils';

/** Region JSON files in public/data/regions/ (used when Supabase is not available) */
const REGION_IDS = [
  'east_anglia',
  'midlands',
  'northern_england',
  'northwest_england',
  'scotland',
  'southeast_england',
  'southwest_england',
  'wales',
];

/**
 * Fetch sites from local JSON region files and merge them.
 * Used in offline/demo mode when Supabase is not configured or unavailable.
 */
export const fetchSitesFromJson = async (): Promise<Site[]> => {
  const allSites: Site[] = [];
  const seen = new Set<string>();

  for (const regionId of REGION_IDS) {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/data/regions/${regionId}.json`);
      if (!res.ok) continue;
      const data = await res.json();
      const list = Array.isArray(data.sites) ? data.sites : [];
      for (const s of list) {
        if (s?.coordinates?.lat == null || s?.coordinates?.lng == null || !s?.name) continue;
        const key = `${s.coordinates.lat},${s.coordinates.lng},${s.name?.siteCode || ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        allSites.push(s as Site);
      }
    } catch (e) {
      console.warn(`Failed to load region ${regionId}.json`, e);
    }
  }

  console.log(`Loaded ${allSites.length} sites from local JSON (offline mode)`);
  return allSites;
};

export const saveSitesToJson = async (region: string, sites: Site[]): Promise<void> => {
  try {
    // Convert sites to JSON string with proper formatting
    const jsonString = JSON.stringify({ sites }, null, 2);
    
    // Create a blob with the JSON data
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${region}.json`;
    
    // Trigger the download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`Successfully saved ${sites.length} sites to ${region}.json`);
  } catch (error) {
    console.error('Error saving sites to JSON:', error);
    throw error;
  }
};

/**
 * Determine the correct table name for a site based on its site code
 */
export const getSiteTableName = (siteCode: string): string => {
  // Default to a sensible table name since we no longer use region-specific tables
  return 'battery_sites'; // This is just used as a fallback
};

/**
 * Update a site in the Supabase database
 * @param site The site to update
 * @returns A promise that resolves when the operation is complete
 */
export const updateSiteInDatabase = async (site: Site): Promise<void> => {
  try {
    // Determine primary table to update based on site type
    let primaryTable = 'battery_sites'; // Default
    
    if (site.name.siteType === 'SDH' || site.sdhInfo) {
      primaryTable = 'sdh_sites';
    } else if (site.name.siteType === 'PDH' || site.pdhInfo) {
      primaryTable = 'pdh_sites';
    } else if (site.name.siteType === 'LOC' || site.locInfo) {
      primaryTable = 'locs_sites';
    } else if (site.name.siteType.includes('TELECOMS') || site.telecomsInfo) {
      primaryTable = 'telecoms_sites';
    } else if (site.name.siteType === 'Signal' || site.name.siteType === 'Sub Signal' || site.name.siteType === 'Points') {
      primaryTable = 'spt_sites';
    } else if (site.name.siteType === 'Fibre' || site.fibreInfo) {
      primaryTable = 'fibre_sites';
    }
    
    // Create a list of all possible tables where this site might exist
    const allTables = [
      'battery_sites',
      'sdh_sites',
      'pdh_sites', 
      'locs_sites',
      'telecoms_sites',
      'spt_sites',
      'fibre_sites'
    ];
    
    // First check which tables actually contain this site code
    const tablesToUpdate = [];
    
    console.log(`Checking for site ${site.name.siteCode} in all tables...`);
    
    for (const table of allTables) {
      try {
        const { count, error } = await supabase
          .from(table)
          .select('*', { count: 'exact', head: true })
          .eq('site_code', site.name.siteCode);
          
        if (error) {
          console.error(`Error checking site in ${table}:`, error);
          continue;
        }
        
        if (count && count > 0) {
          console.log(`Found ${count} instances of site ${site.name.siteCode} in ${table}`);
          tablesToUpdate.push(table);
        }
      } catch (err) {
        console.error(`Error checking table ${table}:`, err);
      }
    }
    
    // If we didn't find the site in any table, default to updating only the primary table
    if (tablesToUpdate.length === 0) {
      console.log(`Site ${site.name.siteCode} not found in any tables, defaulting to ${primaryTable}`);
      tablesToUpdate.push(primaryTable);
    }
    
    // Helper function to get the column structure of a table
    const getTableColumns = async (tableName: string): Promise<string[]> => {
      try {
        // First, try to get a single row to inspect its structure
        const { data, error } = await supabase
          .from(tableName)
          .select('*')
          .limit(1);
        
        if (error || !data || data.length === 0) {
          console.warn(`Could not determine columns for ${tableName}`, error);
          return [];
        }
        
        // Return the column names from the sample row
        return Object.keys(data[0]);
      } catch (err) {
        console.error(`Error getting columns for ${tableName}:`, err);
        return [];
      }
    };
    
    // Update the site in all identified tables
    const updatePromises = tablesToUpdate.map(async (table) => {
      try {
        // Get the available columns for this table
        const columns = await getTableColumns(table);
        
        // Only include fields that exist in this table
        const filteredData: Record<string, any> = {};
        
        // Map site fields to database columns, checking if each column exists
        if (columns.includes('site_code')) filteredData.site_code = site.name.siteCode;
        if (columns.includes('station_name')) filteredData.station_name = site.name.stationName;
        if (columns.includes('site_name')) filteredData.site_name = site.name.stationName;
        if (columns.includes('elr')) filteredData.elr = site.name.elrCode;
        if (columns.includes('lat')) filteredData.lat = site.coordinates.lat;
        if (columns.includes('lng')) filteredData.lng = site.coordinates.lng;
        if (columns.includes('access')) filteredData.access = site.access;
        
        // Only attempt the update if we have data to update
        if (Object.keys(filteredData).length === 0) {
          console.warn(`No updateable fields found for ${table}, skipping`);
          return { table, success: true, skipped: true };
        }
        
        console.log(`Updating site ${site.name.siteCode} in ${table} with fields:`, filteredData);
        
        // Update the table with the filtered data
        const { error } = await supabase
          .from(table)
          .update(filteredData)
          .eq('site_code', site.name.siteCode);
        
        if (error) {
          console.error(`Failed to update site in ${table}:`, error);
          return { table, success: false, error };
        }
        
        console.log(`Successfully updated site ${site.name.siteCode} in table ${table}`);
        return { table, success: true };
      } catch (err) {
        console.error(`Error updating site in ${table}:`, err);
        return { table, success: false, error: err };
      }
    });
    
    // Wait for all updates to complete
    const results = await Promise.all(updatePromises);
    
    // Check if any updates failed
    const failures = results.filter(r => !r.success && !r.skipped);
    
    if (failures.length > 0) {
      console.error(`Failed to update site in ${failures.length} tables:`, failures);
      throw new Error(`Failed to update site in some tables: ${failures.map(f => f.table).join(', ')}`);
    }
    
    console.log(`Successfully updated site ${site.name.siteCode} in all tables:`, 
      results.filter(r => r.success && !r.skipped).map(r => r.table));
  } catch (error) {
    console.error('Error updating site in database:', error);
    throw error;
  }
};

/**
 * Submit a change request for a site
 * @param request The change request details
 * @returns A promise that resolves with the created request ID
 */
export const submitSiteChangeRequest = async (request: Omit<SiteChangeRequest, 'id' | 'created_at'>): Promise<string> => {
  try {
    // Add status if not provided
    const requestWithStatus = {
      ...request,
      status: request.status || 'pending'
    };
    
    // Insert the request into the site_change_requests table
    const { data, error } = await supabase
      .from('site_change_requests')
      .insert(requestWithStatus)
      .select();
    
    if (error) {
      throw new Error(`Failed to submit change request: ${error.message}`);
    }
    
    console.log('Successfully submitted site change request:', data);
    return data?.[0]?.id || '';
  } catch (error) {
    console.error('Error submitting site change request:', error);
    throw error;
  }
};

/**
 * Fetch all pending site change requests
 * @returns A promise that resolves with an array of change requests
 */
export const fetchSiteChangeRequests = async (): Promise<SiteChangeRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('site_change_requests')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      throw new Error(`Failed to fetch change requests: ${error.message}`);
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching site change requests:', error);
    throw error;
  }
};

/**
 * Update the status of a site change request
 * @param requestId The ID of the request to update
 * @param status The new status ('approved' or 'rejected')
 */
export const updateSiteChangeRequestStatus = async (
  requestId: string, 
  status: 'approved' | 'rejected'
): Promise<void> => {
  try {
    console.log(`Updating request ${requestId} to ${status}`);
    
    // Update the request status in the database
    const { error } = await supabase
      .from('site_change_requests')
      .update({ status })
      .eq('id', requestId);
    
    if (error) {
      console.error('Update error details:', error);
      throw new Error(`Failed to update change request status: ${error.message}`);
    }
    
    console.log(`Successfully updated request ${requestId} to ${status}`);
  } catch (error) {
    console.error('Error updating site change request status:', error);
    throw error;
  }
};

/**
 * Fetch battery sites from the battery_sites table
 * Maps DB rows to the Site type expected by the map
 */
export const fetchBatterySites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching battery sites');
    console.log('Supabase client initialized:', !!supabase);
    
    // Fetch battery sites from the battery_sites table with pagination
    let allBatterySites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    console.log('Starting paginated fetch of battery sites');
    
    while (true) {
      console.log(`Fetching battery sites page ${page + 1}, range: ${page * pageSize}-${(page + 1) * pageSize - 1}`);
      
      const { data: pageSites, error } = await supabase
        .from('battery_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching battery sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) {
        console.log(`No more battery sites found after page ${page}`);
        break;
      }
      
      // Add debug logging for the first site to check for the access field
      if (pageSites.length > 0 && page === 0) {
        console.log('Sample battery site data:', JSON.stringify(pageSites[0], null, 2));
        console.log('Has access field:', pageSites[0].hasOwnProperty('access'));
      }
      
      console.log(`Fetched ${pageSites.length} battery sites in page ${page + 1}`);
      allBatterySites = [...allBatterySites, ...pageSites];
      
      // If we got less than pageSize records, we've reached the end
      if (pageSites.length < pageSize) {
        console.log('Reached last page of battery sites');
        break;
      }
      
      page++;
    }
    
    console.log(`Fetched a total of ${allBatterySites.length} battery sites`);
    
    // Map battery sites to the Site type
    const mappedSites: Site[] = allBatterySites
      .map((row: any): Site | null => {
        if (!row) {
          console.warn('Encountered null or undefined row in battery sites');
          return null;
        }

        try {
          // Parse coordinates, handling scientific notation (e.g., "1.23E-02")
          // Excel sometimes exports small numbers in scientific notation
          let lat = parseFloat(String(row.lat).replace(/\s/g, ''));
          let lng = parseFloat(String(row.lng).replace(/\s/g, ''));
          
          // If lat/lng not provided but we have eastings/northings, convert
          if ((!lat || !lng || isNaN(lat) || isNaN(lng)) && row.eastings && row.northings) {
            // Use the simplified conversion for UK grid references
            const eastings = parseFloat(String(row.eastings).replace(/\s/g, ''));
            const northings = parseFloat(String(row.northings).replace(/\s/g, ''));
            
            if (!isNaN(eastings) && !isNaN(northings)) {
              // Simple but reliable conversion for UK coordinates
              // Base coordinates and scaling factors for UK National Grid
              const baseLatitude = 49.9;
              const baseLongitude = -7.5;
              const latFactor = 0.000009;
              const lngFactor = 0.000014;
              
              lat = baseLatitude + (northings * latFactor);
              lng = baseLongitude + (eastings * lngFactor);
            } else {
              console.warn('Invalid eastings/northings values:', row.eastings, row.northings);
              return null;
            }
          }
          
          // Skip if we still don't have valid coordinates
          if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
            console.warn('Invalid coordinates for battery site:', row);
            return null;
          }

          // Determine site type based on site name
          let siteType = 'Battery'; // Default type
          
          const siteName = (row.site_name || '').toUpperCase();
          
          if (siteName.includes('REB')) {
            siteType = 'REB';
          } else if (siteName.includes('TEH')) {
            siteType = 'TEH';
          } else if (siteName.includes('MEH')) {
            siteType = 'MEH';
          } else if (siteName.includes('SIGNAL BOX') || siteName.includes('SIGNALBOX')) {
            siteType = 'SIGNAL BOX';
          } else if (siteName.includes('RELAY ROOM') || siteName.includes('RELAYROOM')) {
            siteType = 'RELAY ROOM';
          } else if (siteName.includes('PSB')) {
            siteType = 'PSB';
          } else if (siteName.includes('PSP')) {
            siteType = 'PSP';
          } else if (siteName.includes('GATE BOX') || siteName.includes('GATEBOX')) {
            siteType = 'GATE BOX';
          } else if (siteName.includes('DEPOT')) {
            siteType = 'DEPOT';
          } else if (siteName.includes('OFFICE')) {
            siteType = 'OFFICE';
          } else if (siteName.includes('STATION')) {
            siteType = 'STATION';
          } else if (siteName.includes('EQUIPMENT ROOM') || siteName.includes('EQUIPMENTROOM')) {
            siteType = 'EQUIPMENT ROOM';
          } else if (siteName.includes('TELECOMS')) {
            siteType = 'TELECOMS EQUIPMENT ROOM';
          } else if (siteName.includes('HABD')) {
            siteType = 'HABD ROOM';
          } else if (siteName.includes('GSMR')) {
            siteType = 'GSMR REPEATER';
          } else if (siteName.includes('TSC')) {
            siteType = 'TSC';
          } else if (siteName.includes('RADIO')) {
            siteType = 'Radio Site';
          }

          // Create site object
          const site: Site = {
            name: {
              stationName: row.site_name || row.item || 'Battery Site',
              siteCode: row.site_code || '',
              elrCode: row.elr || '',
              siteType: siteType, // Use the determined site type
            },
            coordinates: {
              lat,
              lng,
              altitude: 0, // Default altitude if not provided
            },
            // Add battery-specific information
            batteryInfo: {
              item: row.item,
              batteryModel: row.battery_model,
              noCellStrings: row.no_cell_strings,
              noCells: row.no_cells,
              supEquip: row.sup_equip,
              mileage: row.mileage,
              eastings: row.eastings, // Add eastings
              northings: row.northings // Add northings
            },
            access: row.access || 'No access information available',
          };

          return site;
        } catch (err) {
          console.error('Error mapping battery site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped battery sites:', {
      total: mappedSites.length,
      firstSite: mappedSites[0]
    });

    return mappedSites;
  } catch (err) {
    console.error('Error in fetchBatterySites:', err);
    throw err;
  }
};

/**
 * Fetch SDH sites from the sdh_sites table
 * Maps DB rows to the Site type expected by the map
 */
export const fetchSdhSites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching SDH sites');
    
    // Fetch SDH sites from the sdh_sites table with pagination
    let allSdhSites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    console.log('Starting paginated fetch of SDH sites');
    
    while (true) {
      console.log(`Fetching SDH sites page ${page + 1}, range: ${page * pageSize}-${(page + 1) * pageSize - 1}`);
      
      const { data: pageSites, error } = await supabase
        .from('sdh_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching SDH sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) {
        console.log(`No more SDH sites found after page ${page}`);
        break;
      }
      
      console.log(`Fetched ${pageSites.length} SDH sites in page ${page + 1}`);
      allSdhSites = [...allSdhSites, ...pageSites];
      
      // If we got less than pageSize records, we've reached the end
      if (pageSites.length < pageSize) {
        console.log('Reached last page of SDH sites');
        break;
      }
      
      page++;
    }
    
    console.log(`Fetched a total of ${allSdhSites.length} SDH sites`);
    
    // Map SDH sites to the Site type
    const mappedSites: Site[] = allSdhSites
      .map((row: any): Site | null => {
        if (!row) {
          console.warn('Encountered null or undefined row in SDH sites');
          return null;
        }

        try {
          const lat = Number(row.lat);
          const lng = Number(row.lng);
          
          if (isNaN(lat) || isNaN(lng)) {
            console.warn('Invalid coordinates for SDH site:', row);
            return null;
          }

          // Create site object
          const site: Site = {
            name: {
              stationName: row.site_name || row.item || 'SDH Site',
              siteCode: row.site_code || '',
              elrCode: row.elr || '',
              siteType: 'SDH', // Set a special type for SDH sites
            },
            coordinates: {
              lat,
              lng,
              altitude: 0, // Default altitude if not provided
            },
            // Add SDH-specific information
            sdhInfo: {
              item: row.item || '',
              nodeType: row.node_type || '',
              deviceId: row.device_id || '',
              mileage: row.mileage || 0
            },
            access: row.access || 'No access information available',
          };

          return site;
        } catch (err) {
          console.error('Error mapping SDH site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped SDH sites:', {
      total: mappedSites.length,
      firstSite: mappedSites[0]
    });

    return mappedSites;
  } catch (err) {
    console.error('Error in fetchSdhSites:', err);
    throw err;
  }
};

/**
 * Check if a site has multiple equipment items
 * @param siteCode The site code to check
 * @param siteType The type of site (SDH, PDH, etc.)
 * @returns Promise resolving to boolean indicating if the site has multiple equipment
 */
export const hasSiteMultipleEquipment = async (siteCode: string, siteType: string = 'SDH'): Promise<boolean> => {
  try {
    // Determine which table to query based on site type
    const tableName = siteType === 'PDH' ? 'pdh_sites' : 'sdh_sites';
    
    console.log(`Checking if ${siteType} site ${siteCode} has multiple equipment in ${tableName}`);
    
    // Count records for this site code
    const { count, error } = await supabase
      .from(tableName)
      .select('*', { count: 'exact', head: true })
      .eq('site_code', siteCode);
    
    if (error) {
      console.error(`Error checking site equipment count for ${siteType} site:`, error);
      return false;
    }
    
    console.log(`Site ${siteCode} has ${count || 0} equipment records`);
    
    // If we have more than one record for this site code, it has multiple equipment
    return count !== null && count > 1;
  } catch (err) {
    console.error(`Error in hasSiteMultipleEquipment for ${siteType} site:`, err);
    return false;
  }
};

/**
 * Fetch all equipment items for a specific site
 * @param siteCode The site code to fetch equipment for
 * @param siteType The type of site (SDH, PDH, etc.)
 * @returns Promise resolving to an array of equipment items (combining item and device_id)
 */
export const fetchSiteEquipment = async (siteCode: string, siteType: string = 'SDH'): Promise<string[]> => {
  try {
    // Determine which table to query based on site type
    const tableName = siteType === 'PDH' ? 'pdh_sites' : 'sdh_sites';
    
    console.log(`Fetching equipment for ${siteType} site ${siteCode} from ${tableName}`);
    
    // Define types for the response data
    type SdhEquipment = { item: string; device_id: string };
    type PdhEquipment = { item: string; equipment: string; device_id: string };
    
    // Query the database for equipment items
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('site_code', siteCode);
    
    if (error) {
      console.error(`Error fetching ${siteType} site equipment:`, error);
      return [];
    }
    
    // Log results
    console.log(`Fetched ${data?.length || 0} equipment items for ${siteType} site ${siteCode}`);
    
    // If only one item is returned, return empty array (no additional equipment)
    if (!data || data.length <= 1) {
      console.log(`No additional equipment for ${siteType} site ${siteCode}`);
      return [];
    }
    
    // Map the data to formatted equipment strings based on site type
    if (siteType === 'PDH') {
      // For PDH sites, format as "item - equipment (device_id)"
      return (data as PdhEquipment[]).map(row => {
        const parts = [];
        if (row.item) parts.push(row.item);
        if (row.equipment) parts.push(row.equipment);
        
        let result = parts.join(' - ');
        if (row.device_id) {
          result += ` (${row.device_id})`;
        }
        
        return result || 'Unknown equipment';
      });
    } else {
      // For SDH sites, format as "item (device_id)"
      return (data as SdhEquipment[]).map(row => {
        let result = row.item || 'Unknown equipment';
        if (row.device_id) {
          result += ` (${row.device_id})`;
        }
        return result;
      });
    }
  } catch (err) {
    console.error(`Error in fetchSiteEquipment for ${siteType} site:`, err);
    return [];
  }
};

/**
 * Fetch LOC sites from the locs_sites table
 * Maps DB rows to the Site type expected by the map
 */
export const fetchLocSites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching LOC sites');
    
    // Fetch LOC sites from the locs_sites table with pagination
    let allLocSites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    console.log('Starting paginated fetch of LOC sites');
    
    while (true) {
      console.log(`Fetching LOC sites page ${page + 1}, range: ${page * pageSize}-${(page + 1) * pageSize - 1}`);
      
      const { data: pageSites, error } = await supabase
        .from('locs_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching LOC sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) {
        console.log(`No more LOC sites found after page ${page}`);
        break;
      }
      
      console.log(`Fetched ${pageSites.length} LOC sites in page ${page + 1}`);
      allLocSites = [...allLocSites, ...pageSites];
      
      // If we got less than pageSize records, we've reached the end
      if (pageSites.length < pageSize) {
        console.log('Reached last page of LOC sites');
        break;
      }
      
      page++;
    }
    
    console.log(`Fetched a total of ${allLocSites.length} LOC sites`);
    
    // Map LOC sites to the Site type
    const mappedSites: Site[] = allLocSites
      .map((row: any): Site | null => {
        if (!row) {
          console.warn('Encountered null or undefined row in LOC sites');
          return null;
        }

        try {
          const lat = Number(row.lat);
          const lng = Number(row.lng);
          
          if (isNaN(lat) || isNaN(lng)) {
            console.warn('Invalid coordinates for LOC site:', row);
            return null;
          }

          // Create site object
          const site: Site = {
            name: {
              stationName: row.site_name || 'LOC Site',
              siteCode: row.site_code || '',
              elrCode: row.elr || '',
              siteType: 'LOC', // Set a special type for LOC sites
            },
            coordinates: {
              lat,
              lng,
              altitude: 0, // Default altitude if not provided
            },
            // Add LOC-specific information
            locInfo: {
              item: row.item || '',
              locId: row.loc_id || '',
              mileage: row.mileage || 0
            },
            access: row.access || 'No access information available',
          };

          return site;
        } catch (err) {
          console.error('Error mapping LOC site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped LOC sites:', {
      total: mappedSites.length,
      firstSite: mappedSites[0]
    });

    return mappedSites;
  } catch (err) {
    console.error('Error in fetchLocSites:', err);
    throw err;
  }
};

/**
 * Fetch PDH sites from the pdh_sites table
 * Maps DB rows to the Site type expected by the map
 */
export const fetchPdhSites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching PDH sites from pdh_sites table');
    
    // Fetch PDH sites from the pdh_sites table with pagination
    let allPdhSites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    console.log('Starting paginated fetch of PDH sites');
    
    while (true) {
      console.log(`Fetching PDH sites page ${page + 1}, range: ${page * pageSize}-${(page + 1) * pageSize - 1}`);
      
      const { data: pageSites, error } = await supabase
        .from('pdh_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching PDH sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) {
        console.log(`No more PDH sites found after page ${page}`);
        break;
      }
      
      // Add debug logging for the PDH sites
      if (pageSites.length > 0 && page === 0) {
        console.log('Sample PDH site data:', JSON.stringify(pageSites[0], null, 2));
        console.log('Has access field:', pageSites[0].hasOwnProperty('access'));
        
        // Search for site code 448 specifically in the results
        const site448 = pageSites.find(site => site.site_code === '448');
        if (site448) {
          console.log('Found site 448 in PDH sites:', JSON.stringify(site448, null, 2));
          console.log('Site 448 access value:', site448.access);
        }
      }
      
      console.log(`Fetched ${pageSites.length} PDH sites in page ${page + 1}`);
      allPdhSites = [...allPdhSites, ...pageSites];
      
      // If we got less than pageSize records, we've reached the end
      if (pageSites.length < pageSize) {
        console.log('Reached last page of PDH sites');
        break;
      }
      
      page++;
    }
    
    console.log(`Fetched a total of ${allPdhSites.length} PDH sites`);
    
    if (allPdhSites.length === 0) {
      console.warn('No PDH sites found in the database. Check if the pdh_sites table exists and contains data.');
    }
    
    // Map PDH sites to the Site type
    const mappedSites: Site[] = allPdhSites
      .map((row: any): Site | null => {
        if (!row) {
          console.warn('Encountered null or undefined row in PDH sites');
          return null;
        }

        try {
          const lat = Number(row.lat);
          const lng = Number(row.lng);
          
          if (isNaN(lat) || isNaN(lng)) {
            console.warn('Invalid coordinates for PDH site:', row);
            return null;
          }

          // Create site object
          const site: Site = {
            name: {
              stationName: row.site_name || row.item || 'PDH Site',
              siteCode: row.site_code || '',
              elrCode: row.elr || '',
              siteType: 'PDH', // Set a special type for PDH sites
            },
            coordinates: {
              lat,
              lng,
              altitude: 0, // Default altitude if not provided
            },
            // Add PDH-specific information
            pdhInfo: {
              item: row.item || '',
              equipment: row.equipment || '',
              deviceId: row.device_id || '',
              mileage: row.mileage || 0
            },
            access: row.access || 'No access information available',
          };

          return site;
        } catch (err) {
          console.error('Error mapping PDH site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped PDH sites:', {
      total: mappedSites.length,
      firstSite: mappedSites.length > 0 ? mappedSites[0] : null
    });

    return mappedSites;
  } catch (err) {
    console.error('Error in fetchPdhSites:', err);
    throw err;
  }
};

/**
 * Utility function to fetch all available tables (site types) in the database
 * This helps debug which tables actually exist and can be queried
 */
export const fetchAllSiteTypes = async (): Promise<string[]> => {
  try {
    console.log('Fetching all available site tables');
    
    // Call the get_tables function to list all tables in the public schema
    const { data, error } = await supabase
      .rpc('get_tables');
    
    if (error) {
      console.error('Error fetching available tables:', error);
      return [];
    }
    
    console.log('Available tables in database:', data);
    
    // Filter for site-related tables
    const siteTables = (data || []).filter((tableName: string) => 
      tableName.includes('sites') || 
      tableName.includes('site') ||
      tableName === 'pdh_sites' ||
      tableName === 'sdh_sites' ||
      tableName === 'battery_sites' ||
      tableName === 'locs_sites'
    );
    
    console.log('Available site tables:', siteTables);
    return siteTables;
  } catch (err) {
    console.error('Error in fetchAllSiteTypes:', err);
    return [];
  }
};

/**
 * Fetch Telecoms sites from the telecoms_sites table
 * Maps DB rows to the Site type expected by the map
 */
export const fetchTelecomsSites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching Telecoms sites from telecoms_sites table');
    
    // Fetch Telecoms sites from the telecoms_sites table with pagination
    let allTelecomsSites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    console.log('Starting paginated fetch of Telecoms sites');
    
    while (true) {
      console.log(`Fetching Telecoms sites page ${page + 1}, range: ${page * pageSize}-${(page + 1) * pageSize - 1}`);
      
      const { data: pageSites, error } = await supabase
        .from('telecoms_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching Telecoms sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) {
        console.log(`No more Telecoms sites found after page ${page}`);
        break;
      }
      
      console.log(`Fetched ${pageSites.length} Telecoms sites in page ${page + 1}`);
      allTelecomsSites = [...allTelecomsSites, ...pageSites];
      
      // If we got less than pageSize records, we've reached the end
      if (pageSites.length < pageSize) {
        console.log('Reached last page of Telecoms sites');
        break;
      }
      
      page++;
    }
    
    console.log(`Fetched a total of ${allTelecomsSites.length} Telecoms sites`);
    
    // Map Telecoms sites to the Site type
    const mappedSites: Site[] = allTelecomsSites
      .map((row: any): Site | null => {
        if (!row) {
          console.warn('Encountered null or undefined row in Telecoms sites');
          return null;
        }

        try {
          const lat = Number(row.lat);
          const lng = Number(row.lng);
          
          if (isNaN(lat) || isNaN(lng)) {
            console.warn('Invalid coordinates for Telecoms site:', row);
            return null;
          }

          // Determine site type based on site name
          let siteType = 'TELECOMS'; // Default type
          
          const siteName = (row.site_name || '').toUpperCase();
          
          if (siteName.includes('GSMR') || siteName.includes('GSM-R')) {
            siteType = 'GSMR REPEATER';
          } else if (siteName.includes('RADIO') || siteName.includes('MAST')) {
            siteType = 'Radio Site';
          } else if (siteName.includes('EQUIPMENT ROOM') || siteName.includes('EQUIPMENTROOM')) {
            siteType = 'TELECOMS EQUIPMENT ROOM';
          } else if (siteName.includes('CABINET')) {
            siteType = 'TELECOMS CABINET';
          } else if (siteName.includes('FTN')) {
            siteType = 'FTN SITE';
          } else if (siteName.includes('SDH')) {
            siteType = 'SDH';
          } else if (siteName.includes('PDH')) {
            siteType = 'PDH';
          }

          // Create site object
          const site: Site = {
            name: {
              stationName: row.site_name || 'Telecoms Site',
              siteCode: row.site_code || '',
              elrCode: row.elr || '',
              siteType: siteType, // Use the determined site type
            },
            coordinates: {
              lat,
              lng,
              altitude: 0, // Default altitude if not provided
            },
            // Add Telecoms-specific information
            telecomsInfo: {
              item: row.item || '',
              mileage: row.mileage || 0
            },
            access: row.access || 'No access information available',
          };

          return site;
        } catch (err) {
          console.error('Error mapping Telecoms site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped Telecoms sites:', {
      total: mappedSites.length,
      firstSite: mappedSites[0]
    });

    return mappedSites;
  } catch (err) {
    console.error('Error in fetchTelecomsSites:', err);
    throw err;
  }
};

/**
 * Fetch SPT (Signal Post Telephone / Points) sites from Supabase.
 */
export const fetchSptSites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching SPT sites from spt_sites table');
    
    let allSptSites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    while (true) {
      const { data: pageSites, error } = await supabase
        .from('spt_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching SPT sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) break;
      
      allSptSites = [...allSptSites, ...pageSites];
      
      if (pageSites.length < pageSize) break;
      page++;
    }
    
    console.log(`Fetched ${allSptSites.length} SPT sites`);
    
    const mappedSites: Site[] = allSptSites
      .map((row: any): Site | null => {
        if (!row) return null;

        try {
          const lat = Number(row.lat);
          const lng = Number(row.lng);
          
          if (isNaN(lat) || isNaN(lng)) return null;

          const site: Site = {
            name: {
              stationName: row.site_name || 'SPT Site',
              siteCode: row.site_code || '',
              elrCode: row.elr || '',
              siteType: row.site_type || 'Signal',
            },
            coordinates: {
              lat,
              lng,
              altitude: 0,
            },
            access: row.access || 'No access information available',
          };

          return site;
        } catch (err) {
          console.error('Error mapping SPT site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped SPT sites:', { total: mappedSites.length });
    return mappedSites;
  } catch (err) {
    console.error('Error in fetchSptSites:', err);
    throw err;
  }
};

/**
 * Fetch Fibre Cable sites from the fibre_sites table
 * Maps DB rows to the Site type expected by the map
 */
export const fetchFibreSites = async (): Promise<Site[]> => {
  try {
    console.log('Fetching Fibre sites from fibre_sites table');
    
    let allFibreSites: any[] = [];
    let page = 0;
    const pageSize = 1000;
    
    while (true) {
      const { data: pageSites, error } = await supabase
        .from('fibre_sites')
        .select('*')
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error(`Error fetching Fibre sites page ${page + 1}:`, error);
        throw error;
      }
      
      if (!pageSites || !Array.isArray(pageSites) || pageSites.length === 0) break;
      
      allFibreSites = [...allFibreSites, ...pageSites];
      
      if (pageSites.length < pageSize) break;
      page++;
    }
    
    console.log(`Fetched ${allFibreSites.length} Fibre sites`);
    
    const mappedSites: Site[] = allFibreSites
      .map((row: any): Site | null => {
        if (!row) return null;

        try {
          const lat = Number(row.lat);
          const lng = Number(row.lng);
          
          if (isNaN(lat) || isNaN(lng)) return null;

          const site: Site = {
            name: {
              stationName: row.joint_name || 'Fibre Joint',
              siteCode: row.section_number || '',
              elrCode: row.elr || '',
              siteType: 'Fibre',
            },
            coordinates: {
              lat,
              lng,
              altitude: 0,
            },
            access: row.access || 'No access information available',
            fibreInfo: {
              id: row.id,
              cableName: row.cable_name || '',
              location: row.location || '',
              sectionLength: row.section_length || 0,
            },
          };

          return site;
        } catch (err) {
          console.error('Error mapping Fibre site row:', row, err);
          return null;
        }
      })
      .filter((site): site is Site => site !== null);

    console.log('Successfully mapped Fibre sites:', { total: mappedSites.length });
    return mappedSites;
  } catch (err) {
    console.error('Error in fetchFibreSites:', err);
    throw err;
  }
};

/**
 * Debug utility to check which tables contain a specific site code
 * @param siteCode The site code to look for
 * @returns Promise resolving to an object with tables and counts
 */
export const checkSiteInAllTables = async (siteCode: string): Promise<Record<string, number>> => {
  try {
    console.log(`Checking for site ${siteCode} in all database tables...`);
    
    // List of all possible site tables
    const allTables = [
      'battery_sites',
      'sdh_sites',
      'pdh_sites', 
      'locs_sites',
      'telecoms_sites',
      'spt_sites',
      'fibre_sites',
      'sites_east_anglia',
      'sites_midlands',
      'sites_northern_england',
      'sites_northwest_england',
      'sites_scotland',
      'sites_southeast_england',
      'sites_southwest_england',
      'sites_wales'
    ];
    
    // Results object
    const results: Record<string, number> = {};
    
    // Check each table
    for (const table of allTables) {
      try {
        const { count, error } = await supabase
          .from(table)
          .select('*', { count: 'exact', head: true })
          .eq('site_code', siteCode);
          
        if (error) {
          console.error(`Error checking site in ${table}:`, error);
          results[table] = -1; // Indicate error
          continue;
        }
        
        results[table] = count || 0;
        
        if (count && count > 0) {
          console.log(`Found ${count} instances of site ${siteCode} in ${table}`);
          
          // Get the first instance for debugging
          const { data, error: fetchError } = await supabase
            .from(table)
            .select('*')
            .eq('site_code', siteCode)
            .limit(1);
            
          if (!fetchError && data && data.length > 0) {
            console.log(`Sample data from ${table}:`, data[0]);
          }
        }
      } catch (err) {
        console.error(`Error checking table ${table}:`, err);
        results[table] = -1; // Indicate error
      }
    }
    
    console.log(`Site ${siteCode} check complete:`, results);
    return results;
  } catch (error) {
    console.error('Error in checkSiteInAllTables:', error);
    throw error;
  }
};

// ==========================================
// Signal Box Change Request functions
// ==========================================

/**
 * Submit a change request for a signal box
 */
export const submitSignalBoxChangeRequest = async (
  request: Omit<SignalBoxChangeRequest, 'id' | 'created_at'>
): Promise<string> => {
  try {
    const requestWithStatus = {
      ...request,
      status: request.status || 'pending',
    };

    const { data, error } = await supabase
      .from('signal_box_changes')
      .insert(requestWithStatus)
      .select();

    if (error) {
      throw new Error(`Failed to submit signal box change request: ${error.message}`);
    }

    console.log('Successfully submitted signal box change request:', data);
    return data?.[0]?.id || '';
  } catch (error) {
    console.error('Error submitting signal box change request:', error);
    throw error;
  }
};

/**
 * Fetch all signal box change requests
 */
export const fetchSignalBoxChangeRequests = async (): Promise<SignalBoxChangeRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('signal_box_changes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch signal box change requests: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching signal box change requests:', error);
    throw error;
  }
};

/**
 * Update the status of a signal box change request
 */
export const updateSignalBoxChangeRequestStatus = async (
  requestId: string,
  status: 'approved' | 'rejected'
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('signal_box_changes')
      .update({ status })
      .eq('id', requestId);

    if (error) {
      throw new Error(`Failed to update signal box change request status: ${error.message}`);
    }

    console.log(`Successfully updated signal box change request ${requestId} to ${status}`);
  } catch (error) {
    console.error('Error updating signal box change request status:', error);
    throw error;
  }
};

/**
 * Apply an approved signal box change to the signal_boxes table
 */
export const applySignalBoxChange = async (
  signalBoxId: number,
  suggestedInternal?: string,
  suggestedExternal?: string
): Promise<void> => {
  try {
    const updateData: Record<string, string> = {};

    if (suggestedInternal !== undefined && suggestedInternal !== null) {
      updateData.internal_number = suggestedInternal;
    }
    if (suggestedExternal !== undefined && suggestedExternal !== null) {
      updateData.external_number = suggestedExternal;
    }

    if (Object.keys(updateData).length === 0) {
      console.log('No changes to apply');
      return;
    }

    const { error } = await supabase
      .from('signal_boxes')
      .update(updateData)
      .eq('id', signalBoxId);

    if (error) {
      throw new Error(`Failed to update signal box: ${error.message}`);
    }

    console.log(`Successfully updated signal box ${signalBoxId} with:`, updateData);
  } catch (error) {
    console.error('Error applying signal box change:', error);
    throw error;
  }
};

/**
 * Delete a signal box from the database
 */
export const deleteSignalBox = async (signalBoxId: number): Promise<void> => {
  try {
    const { error } = await supabase
      .from('signal_boxes')
      .delete()
      .eq('id', signalBoxId);

    if (error) {
      throw new Error(`Failed to delete signal box: ${error.message}`);
    }

    console.log(`Successfully deleted signal box ${signalBoxId}`);
  } catch (error) {
    console.error('Error deleting signal box:', error);
    throw error;
  }
};

/**
 * Fetch all signal boxes
 */
export const fetchAllSignalBoxes = async (): Promise<{ id: number; name: string; internal_number: string | null; external_number: string | null }[]> => {
  try {
    const { data, error } = await supabase
      .from('signal_boxes')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch signal boxes: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching signal boxes:', error);
    throw error;
  }
};

// ==========================================
// Fibre Cable Change Request functions
// ==========================================

/**
 * Submit a change request for a fibre cable joint
 */
export const submitFibreChangeRequest = async (
  request: Omit<FibreChangeRequest, 'id' | 'created_at'>
): Promise<string> => {
  try {
    const requestWithStatus = {
      ...request,
      status: request.status || 'pending',
    };

    const { data, error } = await supabase
      .from('fibre_changes')
      .insert(requestWithStatus)
      .select();

    if (error) {
      throw new Error(`Failed to submit fibre change request: ${error.message}`);
    }

    console.log('Successfully submitted fibre change request:', data);
    return data?.[0]?.id || '';
  } catch (error) {
    console.error('Error submitting fibre change request:', error);
    throw error;
  }
};

/**
 * Fetch all fibre cable change requests
 */
export const fetchFibreChangeRequests = async (): Promise<FibreChangeRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('fibre_changes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch fibre change requests: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching fibre change requests:', error);
    throw error;
  }
};

/**
 * Update the status of a fibre cable change request
 */
export const updateFibreChangeRequestStatus = async (
  requestId: string,
  status: 'approved' | 'rejected'
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('fibre_changes')
      .update({ status })
      .eq('id', requestId);

    if (error) {
      throw new Error(`Failed to update fibre change request status: ${error.message}`);
    }

    console.log(`Successfully updated fibre change request ${requestId} to ${status}`);
  } catch (error) {
    console.error('Error updating fibre change request status:', error);
    throw error;
  }
};

/**
 * Apply an approved fibre change to the fibre_sites table
 */
export const applyFibreChange = async (request: FibreChangeRequest): Promise<void> => {
  try {
    const updateData: Record<string, any> = {};

    if (request.suggested_joint_name) updateData.joint_name = request.suggested_joint_name;
    if (request.suggested_cable_name) updateData.cable_name = request.suggested_cable_name;
    if (request.suggested_section_number) updateData.section_number = request.suggested_section_number;
    if (request.suggested_elr) updateData.elr = request.suggested_elr;
    if (request.suggested_location) updateData.location = request.suggested_location;
    if (request.suggested_access) updateData.access = request.suggested_access;
    if (request.suggested_lat != null) updateData.lat = request.suggested_lat;
    if (request.suggested_lng != null) updateData.lng = request.suggested_lng;

    if (Object.keys(updateData).length === 0) {
      console.log('No fibre changes to apply');
      return;
    }

    const { error } = await supabase
      .from('fibre_sites')
      .update(updateData)
      .eq('id', request.fibre_site_id);

    if (error) {
      throw new Error(`Failed to update fibre site: ${error.message}`);
    }

    console.log(`Successfully updated fibre site ${request.fibre_site_id} with:`, updateData);
  } catch (error) {
    console.error('Error applying fibre change:', error);
    throw error;
  }
};

/**
 * Insert a new fibre joint into the fibre_sites table (admin only, after approval)
 */
export const insertNewFibreSite = async (request: FibreChangeRequest): Promise<void> => {
  try {
    const newSite: Record<string, any> = {};

    if (request.suggested_cable_name) newSite.cable_name = request.suggested_cable_name;
    if (request.suggested_joint_name) newSite.joint_name = request.suggested_joint_name;
    if (request.suggested_section_number) newSite.section_number = request.suggested_section_number;
    if (request.suggested_elr) newSite.elr = request.suggested_elr;
    if (request.suggested_location) newSite.location = request.suggested_location;
    if (request.suggested_access) newSite.access = request.suggested_access;
    if (request.suggested_lat != null) newSite.lat = request.suggested_lat;
    if (request.suggested_lng != null) newSite.lng = request.suggested_lng;

    if (!newSite.lat || !newSite.lng) {
      throw new Error('Latitude and longitude are required to create a new fibre joint');
    }

    const { error } = await supabase
      .from('fibre_sites')
      .insert(newSite);

    if (error) {
      throw new Error(`Failed to create new fibre site: ${error.message}`);
    }

    console.log('Successfully created new fibre site:', newSite);
  } catch (error) {
    console.error('Error creating new fibre site:', error);
    throw error;
  }
};

/**
 * Debug utility function for browser console to check a specific site
 * @param siteCode The site code to debug
 */
export const debugSiteFromConsole = async (siteCode: string): Promise<void> => {
  try {
    console.log(`Debugging site ${siteCode}...`);
    
    // Check which tables contain this site
    const tablesResult = await checkSiteInAllTables(siteCode);
    console.log('Site exists in these tables:', tablesResult);
    
    // Check the fetchSdhSites and fetchTelecomsSites for this site
    console.log('\nChecking site in SDH data...');
    const sdhSites = await fetchSdhSites();
    const sdhSite = sdhSites.find(site => site.name.siteCode === siteCode);
    console.log('Found in SDH sites:', sdhSite);
    
    console.log('\nChecking site in Telecoms data...');
    const telecomsSites = await fetchTelecomsSites();
    const telecomsSite = telecomsSites.find(site => site.name.siteCode === siteCode);
    console.log('Found in Telecoms sites:', telecomsSite);
    
    console.log('\nDebug complete');
  } catch (err) {
    console.error('Error in debugSiteFromConsole:', err);
  }
};

// ==========================================
// SPT Circuits (Doncaster HiPath Allocations)
// ==========================================

export interface SptCircuit {
  id: number;
  circuit_name: string;
  circuit_type: string;
  status: 'faulty' | 'fixed' | 'not_reported';
  hipath_vert: string | null;
  hipath_row: number | null;
  hipath_pair: number | null;
  cable_pair_no: string | null;
  cable_vert: string | null;
  cable_row: number | null;
  cable_pair: number | null;
  tie_cable: string | null;
  notes: string | null;
  updated_at: string;
  created_at: string;
}

export interface SptCircuitChangeRequest {
  id?: string;
  circuit_id: number;
  circuit_name: string;
  user_email: string;
  suggested_status?: string;
  suggested_hipath_vert?: string;
  suggested_hipath_row?: number;
  suggested_hipath_pair?: number;
  suggested_cable_pair_no?: string;
  suggested_cable_vert?: string;
  suggested_cable_row?: number;
  suggested_cable_pair?: number;
  suggested_tie_cable?: string;
  suggested_notes?: string;
  change_notes?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at?: string;
}

/**
 * Fetch all SPT circuits
 */
export const fetchSptCircuits = async (): Promise<SptCircuit[]> => {
  try {
    const { data, error } = await supabase
      .from('spt_circuits')
      .select('*')
      .order('circuit_name', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch SPT circuits: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching SPT circuits:', error);
    throw error;
  }
};

/**
 * Submit a change request for an SPT circuit
 */
export const submitSptCircuitChangeRequest = async (
  request: Omit<SptCircuitChangeRequest, 'id' | 'created_at'>
): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('spt_circuit_changes')
      .insert([request])
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to submit change request: ${error.message}`);
    }

    return data.id;
  } catch (error) {
    console.error('Error submitting SPT circuit change request:', error);
    throw error;
  }
};

/**
 * Fetch all SPT circuit change requests
 */
export const fetchSptCircuitChangeRequests = async (): Promise<SptCircuitChangeRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('spt_circuit_changes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch change requests: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching SPT circuit change requests:', error);
    throw error;
  }
};

/**
 * Update the status of an SPT circuit change request
 */
export const updateSptCircuitChangeRequestStatus = async (
  requestId: string,
  status: 'approved' | 'rejected'
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('spt_circuit_changes')
      .update({ status })
      .eq('id', requestId);

    if (error) {
      throw new Error(`Failed to update request status: ${error.message}`);
    }
  } catch (error) {
    console.error('Error updating SPT circuit change request status:', error);
    throw error;
  }
};

/**
 * Apply an approved SPT circuit change to the spt_circuits table
 */
export const applySptCircuitChange = async (request: SptCircuitChangeRequest): Promise<void> => {
  try {
    const updates: Record<string, any> = { updated_at: new Date().toISOString() };

    if (request.suggested_status) updates.status = request.suggested_status;
    if (request.suggested_hipath_vert !== undefined) updates.hipath_vert = request.suggested_hipath_vert;
    if (request.suggested_hipath_row !== undefined) updates.hipath_row = request.suggested_hipath_row;
    if (request.suggested_hipath_pair !== undefined) updates.hipath_pair = request.suggested_hipath_pair;
    if (request.suggested_cable_pair_no !== undefined) updates.cable_pair_no = request.suggested_cable_pair_no;
    if (request.suggested_cable_vert !== undefined) updates.cable_vert = request.suggested_cable_vert;
    if (request.suggested_cable_row !== undefined) updates.cable_row = request.suggested_cable_row;
    if (request.suggested_cable_pair !== undefined) updates.cable_pair = request.suggested_cable_pair;
    if (request.suggested_tie_cable !== undefined) updates.tie_cable = request.suggested_tie_cable;
    if (request.suggested_notes !== undefined) updates.notes = request.suggested_notes;

    const { error } = await supabase
      .from('spt_circuits')
      .update(updates)
      .eq('id', request.circuit_id);

    if (error) {
      throw new Error(`Failed to apply circuit change: ${error.message}`);
    }
  } catch (error) {
    console.error('Error applying SPT circuit change:', error);
    throw error;
  }
};